// 還沒加入的動畫師的邀請連結：剛建好就跳出來；之後按「邀請連結」再拿。
// 邀請碼後台只存雜湊，拿不回舊的：這次打開頁面建過的還記著，其他情況要重新產生（舊連結同時作廢）。
import { useCallback, useRef, useState } from 'react';
import { Check, Copy, Link, X } from 'lucide-react';
import * as api from '../lib/api';
import type { Invite } from '../lib/api';
import { buildInviteMessage } from '../lib/inviteMessage';
import { inviteUrl } from '../lib/siteUrl';
import { copyToClipboard } from '../lib/clipboard';
import { toast } from '../lib/toast';
import { friendlyError } from '../lib/songReview';
import { useEscapeClose } from '../lib/useEscapeClose';
import { confirmDialog } from './dialogs';
import Icon from './Icon';
import './NewProjectDialog.css';
import './InviteDialog.css';

const LINK_DAYS = 7;

interface Shown {
  personName: string;
  url: string;
  message: string;
}

function LinkDialog({ shown, onClose }: { shown: Shown; onClose: () => void }) {
  useEscapeClose(true, onClose);
  const [copied, setCopied] = useState<'message' | 'link' | null>(null);
  async function copy(text: string, what: 'message' | 'link') {
    if (await copyToClipboard(text)) setCopied(what);
    else toast.error('複製失敗，請自己選取複製');
  }
  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog acct-dialog" role="dialog" aria-modal="true" aria-label="邀請連結" onClick={(e) => e.stopPropagation()}>
        <header className="dialog__header">
          <h2><Icon icon={Link} size="md" /> 給「{shown.personName}」的邀請連結</h2>
          <button className="dialog__close" onClick={onClose} title="關閉" aria-label="關閉"><Icon icon={X} size="md" /></button>
        </header>
        <div className="dialog__body">
          <div className="acct-result" data-testid="pending-invite-result">
            <textarea readOnly value={shown.message} aria-label="要傳給對方的訊息" onFocus={(e) => e.currentTarget.select()} />
            <div className="acct-result__row">
              <button type="button" className="btn btn--primary acct-btn" onClick={() => void copy(shown.message, 'message')} autoFocus>
                <Icon icon={copied === 'message' ? Check : Copy} /> {copied === 'message' ? '已複製訊息' : '複製訊息'}
              </button>
              <button type="button" className="btn btn--ghost acct-btn" onClick={() => void copy(shown.url, 'link')}>
                <Icon icon={copied === 'link' ? Check : Link} /> {copied === 'link' ? '已複製連結' : '只複製連結'}
              </button>
            </div>
            <div className="acct-result__link" data-testid="pending-invite-url">{shown.url}</div>
            <p className="acct-result__note">他加入之後，指派給他的歌就在他的工作站。</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 拿連結、顯示連結。dialog 放進畫面裡；remember = 剛建好一條（顯示並記著）；showLink = 按「邀請連結」 */
export function usePendingInviteLink({ projectId, projectName, onChanged }: { projectId: string; projectName: string; onChanged?: () => void }) {
  const [shown, setShown] = useState<Shown | null>(null);
  const known = useRef(new Map<string, Shown>());

  const remember = useCallback((userId: string, invite: Invite, token: string) => {
    const url = inviteUrl(token);
    const next: Shown = {
      personName: invite.personName,
      url,
      message: buildInviteMessage({ personName: invite.personName, projectName: invite.projectName || projectName, url, days: LINK_DAYS, maxUses: 1 }),
    };
    known.current.set(userId, next);
    setShown(next);
  }, [projectName]);

  const showLink = useCallback(async (member: { id: string; name: string; inviteOpen?: boolean }) => {
    const had = known.current.get(member.id);
    if (had && member.inviteOpen !== false) { setShown(had); return; }
    if (member.inviteOpen && !(await confirmDialog({
      title: `重新產生「${member.name}」的連結？`,
      message: '連結只會顯示一次。產生新的之後，之前給他的連結就不能用了。',
      confirmLabel: '重新產生',
    }))) return;
    try {
      const { invite, token } = await api.createInvite(projectId, { role: 'animator', userId: member.id, replaceOpen: true });
      remember(member.id, invite, token);
      onChanged?.();
    } catch (e) {
      toast.error('連結沒有建好：' + friendlyError(e));
    }
  }, [projectId, remember, onChanged]);

  const dialog = shown ? <LinkDialog shown={shown} onClose={() => setShown(null)} /> : null;
  return { remember, showLink, dialog };
}
